/* RESTORE180 PDP — Sticky Add-to-Cart banner behavior (section 13)
   Source file — `vp-sticky-atc.min.js` is the built sibling referenced in liquid.

   - Shows the banner once the hero/main Add-to-Cart button scrolls out of view
     upward; hides it once the stop sentinel (reviews section) scrolls into view.
   - Adds the SAME variant / selling plan currently selected in the main product
     form by proxy-submitting that form. This reuses the theme's <product-form>
     custom element (variant + selling-plan state, cart drawer/notification,
     error handling) instead of re-implementing /cart/add.js — the robust choice.
   - Respects prefers-reduced-motion (handled in CSS), focus, and modal layering.
   - Multiple-instance safe.
*/
(function () {
  if (customElements.get('vp-sticky-atc')) return;

  class VPStickyATC extends HTMLElement {
    constructor() {
      super();
      this._state = null; // 'hidden' | 'sticky' | 'section'
      this._startOut = false; // start sentinel is out of view (above)
      this._stopIn = false; // deprecated
      this._customStopIn = false;
      this._customStopEls = new Set(); // tracks which stop sentinels are in viewport
      this._parentStopIn = false;
      this._startObserver = null;
      this._customObserver = null;
      this._parentObserver = null;
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.disabled === 'true') {
        this.remove();
        return;
      }

      this.button = this.querySelector('[data-vp-sticky-atc-button]');
      this.startThreshold = parseInt(this.dataset.startOffset || '0', 10);

      // Reveal the element to the layout (it stays translateY'd off-screen via CSS).
      this.removeAttribute('hidden');

      if (this.button) {
        this.button.addEventListener('click', this._onClick);
      }

      // Defer observer setup until the rest of the page (hero form, reviews) is in DOM.
      if (document.readyState === 'complete') {
        this._init();
      } else {
        window.addEventListener('load', () => this._init(), { once: true });
        // also try immediately in case load already fired between checks
        requestAnimationFrame(() => this._init());
      }
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._onClick);
      if (this._startObserver) this._startObserver.disconnect();
      if (this._customObserver) this._customObserver.disconnect();
      if (this._parentObserver) this._parentObserver.disconnect();
    }

    _init() {
      if (this._inited) return;
      
      const startEl = this._resolveStartEl();
      
      const sel = (this.dataset.stopSelector || '').trim();
      const customEls = (sel && sel !== 'PARENT_SECTION')
        ? sel.split(',').map(s => safeQuery(s.trim())).filter(Boolean)
        : [];
      const parentEl = this.closest('.shopify-section') || this.parentElement;

      if (!startEl && !customEls.length && !parentEl) {
        if (!this._retried) {
          this._retried = true;
          setTimeout(() => this._init(), 500);
        }
        return;
      }
      this._inited = true;

      // ponytail: sync snapshot so _update() gets correct state immediately on
      // page load at any scroll position (fixes scroll-restoration race with IO).
      if (startEl) {
        const r = startEl.getBoundingClientRect();
        this._startOut = r.bottom < 0;
      } else {
        this._startOut = true;
      }
      if (parentEl) {
        const r = parentEl.getBoundingClientRect();
        this._parentStopIn = r.top < window.innerHeight && r.bottom > 0;
      }
      if (customEls.length) {
        this._customStopIn = customEls.some((el) => {
          const r = el.getBoundingClientRect();
          return r.top < window.innerHeight && r.bottom > 0;
        });
      }

      if (startEl) {
        const rootMargin = `0px 0px ${-this.startThreshold}px 0px`;
        this._startObserver = new IntersectionObserver(
          (entries) => {
            const e = entries[0];
            // start sentinel considered "out (above)" when not intersecting and
            // its bounding box is above the viewport top.
            this._startOut = !e.isIntersecting && e.boundingClientRect.top < 0;
            this._update();
          },
          { root: null, threshold: 0, rootMargin }
        );
        this._startObserver.observe(startEl);
      } else {
        this._startOut = true;
      }

      if (customEls.length) {
        this._customObserver = new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              if (e.isIntersecting) this._customStopEls.add(e.target);
              else this._customStopEls.delete(e.target);
            }
            this._customStopIn = this._customStopEls.size > 0;
            this._update();
          },
          { root: null, threshold: 0 }
        );
        customEls.forEach(el => this._customObserver.observe(el));
      }

      if (parentEl) {
        this._parentObserver = new IntersectionObserver(
          (entries) => {
            const e = entries[0];
            this._parentStopIn = e.isIntersecting;
            this._update();
          },
          { root: null, threshold: 0 }
        );
        this._parentObserver.observe(parentEl);
      }

      this._update();
    }

    _resolveStartEl() {
      const sel = (this.dataset.startSelector || '').trim();
      if (sel) {
        const el = safeQuery(sel);
        if (el) return el;
      }
      // Fallback: the main product section's submit button (the hero ATC).
      const sectionId = (this.dataset.targetSection || 'main-product').trim();
      return (
        document.getElementById('ProductSubmitButton-' + sectionId) ||
        document.querySelector(
          '#' + cssEscape(sectionId) + ' [type="submit"][name="add"], .product-form__submit'
        )
      );
    }

    _resolveStopEl() {
      // not used anymore, but keep for fallback or safety if needed,
      // actually we don't need it because we resolve directly in _init().
    }

    _update() {
      let newState = 'hidden';
      if (this._startOut) {
        if (this._parentStopIn) {
          newState = 'section';
        } else if (this._customStopIn) {
          newState = 'hidden';
        } else {
          newState = 'sticky';
        }
      }

      if (newState === this._state) return;
      this._state = newState;
      if (newState === 'section') {
        this.setAttribute('data-section-mode', '');
        this.removeAttribute('data-visible');
      } else if (newState === 'sticky') {
        this.removeAttribute('data-section-mode');
        this.setAttribute('data-visible', '');
      } else {
        this.removeAttribute('data-section-mode');
        this.removeAttribute('data-visible');
      }
    }

    _onClick(evt) {
      evt.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;

      const form = this._resolveMainForm();
      if (!form) {
        // No main form available: hard fallback to the product page.
        window.location.href = '/cart';
        return;
      }

      this._setLoading(true);

      const submitBtn = form.querySelector('[type="submit"][name="add"]');

      // Re-enable button loading reset once the main form finishes (cart drawer
      // opening or error). product-form.js removes 'loading' from its own button;
      // we mirror that by listening for the drawer/notification or a timeout.
      const done = () => this._setLoading(false);
      document.body.addEventListener('vp:sticky-atc:reset', done, { once: true });
      // Safety timeout so the spinner never sticks.
      setTimeout(done, 6000);
      // When the cart drawer / notification renders, clear loading.
      this._watchCartRender(done);

      // requestSubmit triggers the form's 'submit' event so <product-form>'s
      // handler runs with the CURRENT variant id + selling plan + quantity.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitBtn || undefined);
      } else if (submitBtn) {
        submitBtn.click();
      } else {
        form.submit();
      }
    }

    _watchCartRender(done) {
      const targets = [
        document.querySelector('cart-drawer'),
        document.getElementById('cart-notification'),
      ].filter(Boolean);
      if (!targets.length) {
        setTimeout(done, 1200);
        return;
      }
      const obs = new MutationObserver(() => {
        const open = targets.some(
          (t) =>
            t.classList.contains('active') ||
            t.classList.contains('animate') ||
            t.getAttribute('aria-hidden') === 'false' ||
            !t.classList.contains('is-empty')
        );
        if (open) {
          done();
          obs.disconnect();
        }
      });
      targets.forEach((t) =>
        obs.observe(t, { attributes: true, attributeFilter: ['class', 'aria-hidden'] })
      );
      setTimeout(() => obs.disconnect(), 6000);
    }

    _resolveMainForm() {
      const sectionId = (this.dataset.targetSection || 'main-product').trim();
      // Prefer the section's <product-form> > form.
      const productForm =
        document.querySelector('#' + cssEscape(sectionId) + ' product-form form') ||
        document.querySelector('product-form form');
      if (productForm) return productForm;
      return (
        document.querySelector('form[action$="/cart/add"]') ||
        document.querySelector('[data-type="add-to-cart-form"]')
      );
    }

    _setLoading(state) {
      if (!this.button) return;
      if (state) {
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');
        const sp = this.button.querySelector('.vp-sticky-atc__spinner');
        if (sp) sp.hidden = false;
      } else {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
        const sp = this.button.querySelector('.vp-sticky-atc__spinner');
        if (sp) sp.hidden = true;
      }
    }
  }

  function safeQuery(sel) {
    try {
      return document.querySelector(sel);
    } catch (e) {
      return null;
    }
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  customElements.define('vp-sticky-atc', VPStickyATC);
})();
